/**
 * API client functions for the Archon Web UI.
 * Uses relative URLs - Vite proxy handles routing in dev.
 * SSE streams bypass the proxy in dev mode (Vite proxy buffers SSE responses).
 */
import type { WorkflowRunStatus } from '@/lib/types';
import type { components } from '@/lib/api.generated';
import type { WorkflowAdmissionPolicyRecord } from '@/lib/workflow-admission-policy';

export type WorkflowDefinition = components['schemas']['WorkflowDefinition'];
export type DagNode = components['schemas']['DagNode'];

// Boot-state shape matches server-side `packages/server/src/boot/readiness.ts`
// and the OpenAPI schema at `packages/server/src/routes/schemas/boot.ts`.
// Inlined rather than re-exported from `components['schemas']` because the
// generated index-signature resolution tripped
// `@typescript-eslint/no-redundant-type-constituents` on consumers that
// wrote `BootState | undefined`. Schema is small and stable; keep in sync.
export type BootPhase = 'starting' | 'verifying' | 'ready' | 'degraded' | 'failed';
export type BootCheckPhase = 'pending' | 'running' | 'ok' | 'degraded' | 'failed';
export interface BootCheckResult {
  status: BootCheckPhase;
  detail?: string;
  durationMs?: number;
}
export interface BootState {
  phase: BootPhase;
  startedAt: string;
  completedAt?: string;
  checks: Record<string, BootCheckResult>;
}

export function parseBootStateResponse(status: number, value: unknown): BootState {
  if (status !== 200 && status !== 503) {
    throw new Error(`Readiness endpoint returned unexpected status ${String(status)}`);
  }
  if (typeof value !== 'object' || value === null) {
    throw new Error('Readiness endpoint returned an invalid body');
  }
  const candidate = value as Record<string, unknown>;
  const phases: readonly BootPhase[] = ['starting', 'verifying', 'ready', 'degraded', 'failed'];
  if (!phases.includes(candidate.phase as BootPhase) || typeof candidate.startedAt !== 'string') {
    throw new Error('Readiness endpoint returned an invalid phase or timestamp');
  }
  if (candidate.completedAt !== undefined && typeof candidate.completedAt !== 'string') {
    throw new Error('Readiness endpoint returned an invalid completion timestamp');
  }
  if (typeof candidate.checks !== 'object' || candidate.checks === null) {
    throw new Error('Readiness endpoint returned invalid checks');
  }
  const checkPhases: readonly BootCheckPhase[] = ['pending', 'running', 'ok', 'degraded', 'failed'];
  for (const result of Object.values(candidate.checks as Record<string, unknown>)) {
    if (typeof result !== 'object' || result === null) {
      throw new Error('Readiness endpoint returned an invalid check result');
    }
    const check = result as Record<string, unknown>;
    if (!checkPhases.includes(check.status as BootCheckPhase)) {
      throw new Error('Readiness endpoint returned an invalid check status');
    }
    if (check.detail !== undefined && typeof check.detail !== 'string') {
      throw new Error('Readiness endpoint returned invalid check detail');
    }
    if (
      check.durationMs !== undefined &&
      (typeof check.durationMs !== 'number' || !Number.isFinite(check.durationMs))
    ) {
      throw new Error('Readiness endpoint returned an invalid check duration');
    }
  }
  return value as BootState;
}

export type LiveDeploymentStepStatus =
  | 'pending'
  | 'running'
  | 'passed'
  | 'failed'
  | 'blocked'
  | 'skipped';

export interface LiveDeploymentAttempt {
  id: string;
  status: LiveDeploymentStepStatus;
  attemptedAt: string;
  notes: string | null;
  output: string | null;
  failureReason: string | null;
  nextAction: string | null;
}

export interface LiveDeploymentStep {
  id: string;
  label: string;
  description: string;
  command: string | null;
  evidenceIds: string[];
  status: LiveDeploymentStepStatus;
  attempts: LiveDeploymentAttempt[];
}

export interface LiveDeploymentEvidence {
  id: string;
  label: string;
  file: string;
  path: string;
  exists: boolean;
  template: boolean;
  valid: boolean;
  summary: string;
}

export interface LiveDeploymentSnapshot {
  outcomeId: string;
  status: 'blocked' | 'ready_to_complete';
  completionReady: boolean;
  blockers: string[];
  steps: LiveDeploymentStep[];
  evidence: LiveDeploymentEvidence[];
}

export interface LiveDeploymentAttemptInput {
  status: LiveDeploymentStepStatus;
  notes?: string;
  output?: string;
  failureReason?: string;
  nextAction?: string;
}

export interface LiveDeploymentCompletionResult {
  status: 'blocked' | 'ready_to_complete';
  completionReady: boolean;
  blockers: string[];
}

/**
 * Base URL for SSE streams. Must be SAME-ORIGIN as the page so the browser
 * sends the session cookie: the EventSource is created without `withCredentials`,
 * so a cross-origin URL (e.g. hitting the dev API port directly) omits the
 * cookie and the stream 401s. In dev this routes through the Vite proxy
 * (relative URL); in production the web UI is served from the API origin.
 * Both collapse to API_PREFIX.
 *
 * API_PREFIX accounts for base-path deployments (e.g. /maestro under a reverse
 * proxy). When VITE_BASE_PATH is set to '/maestro/', fetch calls need to go to
 * '/maestro/api/*' so the edge routes them back to the app backend. For
 * root-hosted installs this stays ''.
 */
const rawBasePath = (import.meta.env.VITE_BASE_PATH as string | undefined) ?? '/';
const normalizedBase = rawBasePath.replace(/\/$/, '');
export const API_PREFIX = normalizedBase && normalizedBase !== '' ? normalizedBase : '';
export const SSE_BASE_URL = API_PREFIX;

export interface ConversationResponse {
  id: string;
  platform_type: string;
  platform_conversation_id: string;
  codebase_id: string | null;
  cwd: string | null;
  ai_assistant_type: string;
  title: string | null;
  last_activity_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectSourceRecord {
  kind: 'local_path' | 'git_url' | 'internal_reference';
  provider: 'filesystem' | 'git' | 'github' | 'gitea';
  baselineId?: string;
  repositoryUrl?: string;
  localMirrorPath?: string;
  ref?: string;
  seedRevision?: string;
  visibility?: 'internal' | 'released';
  [key: string]: unknown;
}

export interface ProjectSourceCatalogEntry extends ProjectSourceRecord {
  id: string;
  label: string;
  description: string;
  defaultRef?: string;
  recommended?: boolean;
  sortOrder?: number;
  status?: 'active' | 'archived';
  registered?: boolean;
  registeredCodebaseId?: string;
  registeredDefaultCwd?: string;
  registeredAt?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CodebaseResponse {
  id: string;
  name: string;
  repository_url: string | null;
  project_source: ProjectSourceRecord | null;
  default_cwd: string;
  ai_assistant_type: string;
  allow_env_keys: boolean;
  workflow_admission_policy: WorkflowAdmissionPolicyRecord | null;
  workflow_audit_presets: WorkflowAuditSavedPreset[] | null;
  commands: Record<string, { path: string; description: string }>;
  registration_receipt?: {
    codebase_id: string;
    codebase_already_existed: boolean;
    project_id: string;
    project_code: string;
    project_name: string;
    project_created: boolean;
    project_link_role: 'primary' | 'member';
    source_provider: string | null;
    repository_url: string | null;
  };
  created_at: string;
  updated_at: string;
}

export type WorkRequestType =
  | 'bug'
  | 'change_request'
  | 'feature'
  | 'design'
  | 'support'
  | 'deployment'
  | 'ops'
  | 'question'
  | 'other';
export type WorkRequestState =
  | 'new'
  | 'triaging'
  | 'planned'
  | 'in_progress'
  | 'blocked'
  | 'review'
  | 'delivered'
  | 'closed';
export type WorkRequestPriority = 'low' | 'normal' | 'high' | 'urgent';
export type WorkRequestSyncStatus = 'pending' | 'synced' | 'failed' | 'blocked';
export type WorkRequestActivityType =
  | 'created'
  | 'gitea_synced'
  | 'gitea_sync_failed'
  | 'comment'
  | 'dispatch'
  | 'state_changed'
  | 'delivered'
  | 'outcome_created';

export interface WorkRequestActivityEvent {
  type: WorkRequestActivityType;
  message: string;
  actor?: string;
  createdAt?: string;
  at?: string;
  data?: Record<string, unknown>;
}

export interface WorkRequestResponse {
  id: string;
  title: string;
  description: string | null;
  request_type: WorkRequestType;
  state: WorkRequestState;
  priority: WorkRequestPriority;
  customer_name: string | null;
  requester_name: string | null;
  requester_contact: string | null;
  project_id: string | null;
  primary_codebase_id: string | null;
  conversation_id: string | null;
  linked_outcome_ids: string[];
  linked_workflow_run_ids: string[];
  gitea_owner: string | null;
  gitea_repo: string | null;
  gitea_issue_number: number | null;
  gitea_issue_url: string | null;
  gitea_issue_state: string | null;
  sync_status: WorkRequestSyncStatus;
  sync_error: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface CreateWorkRequestInput {
  title: string;
  description?: string | null;
  requestType: WorkRequestType;
  priority?: WorkRequestPriority;
  customerName?: string | null;
  requesterName?: string | null;
  requesterContact?: string | null;
  projectId?: string | null;
  primaryCodebaseId?: string | null;
  conversationId?: string | null;
}

export interface LocalEnrollmentSummary {
  id: string;
  status: string;
  manifestHash: string;
}

export interface LocalEnrollmentRemediation {
  type: string;
  status: string;
}

export interface PrepareLocalEnrollmentInput {
  deviceId: string;
  environment: string;
  publicUrl: string;
  backendPort: number;
  webPort: number;
}

export interface PrepareLocalEnrollmentResult {
  enrollment: LocalEnrollmentSummary;
  summaryMarkdown: string;
  manifestYaml: string;
  manifestHash: string;
  manifestVersion: number;
  blockers: string[];
  autoRemediations: LocalEnrollmentRemediation[];
}

export interface ApproveLocalEnrollmentResult {
  enrollment: LocalEnrollmentSummary;
}

export type LocalEnrollmentAuthorityStatus = 'pending' | 'approved' | 'ready' | 'revoked' | 'error';
export type LocalEnrollmentAuthorityReleaseStatus =
  | 'pending'
  | 'ready'
  | 'redeemed'
  | 'expired'
  | 'revoked'
  | 'error';
export type LocalEnrollmentActivationApprovalStatus = 'pending' | 'approved';

export interface LocalEnrollmentAuthorityState {
  enrollmentId: string;
  mosAuthorityRequestId: string | null;
  authorityStatus: LocalEnrollmentAuthorityStatus;
  releaseStatus: LocalEnrollmentAuthorityReleaseStatus;
  activationApprovalStatus: LocalEnrollmentActivationApprovalStatus;
  authorityClientId: string | null;
  releaseExpiresAt: string | null;
  lastSyncedAt: string | null;
  authorityCorrelationId: string | null;
  releaseCorrelationId: string | null;
  activationApprovedBy: string | null;
  activationApprovedAt: string | null;
}

export interface LocalEnrollmentAuthorityStateResult {
  authority: LocalEnrollmentAuthorityState;
}

export interface LocalEnrollmentTokenResult {
  enrollmentId: string;
  deviceId: string;
  token: string;
  expiresAt: string;
  command: string;
}

export interface LocalEnrollmentDockerReadiness {
  status: number;
  ok: boolean;
  phase?: 'starting' | 'verifying' | 'ready' | 'degraded' | 'failed';
  providerAuth?: {
    status?: 'pending' | 'running' | 'ok' | 'degraded' | 'failed';
    detail?: string | null;
  } | null;
}

export interface LocalEnrollmentDockerLaunchResult {
  enrollmentId: string;
  deviceId: string;
  command: string;
  composeFile: string;
  image: string;
  readiness: LocalEnrollmentDockerReadiness;
}

export interface LocalEnrollmentDoctorCheck {
  checks: {
    name: string;
    ok: boolean;
    detail: string;
    severity?: 'error' | 'warn' | 'info';
  }[];
  probes: {
    name: string;
    url: string;
    ok: boolean;
    detail?: string;
    error?: string;
    status?: number;
    httpStatus?: number;
    payloadStatus?: string;
    ms?: number;
  }[];
  summary: {
    ok: boolean;
    failed: string[];
  };
}

export interface LocalEnrollmentDoctorCheckResult {
  enrollmentId: string;
  deviceId: string;
  federationKeyId: string;
  cloudSessionId: string | null;
  doctor: LocalEnrollmentDoctorCheck;
}

export interface LocalEnrollmentFederationCheck {
  ok: boolean;
  checkedAt: string;
  migration: {
    ok: boolean;
    reason: string;
  };
  env: Record<string, unknown>;
  probes: {
    name: string;
    url: string;
    ok: boolean;
    httpStatus?: number;
    payloadStatus?: string;
    error?: string;
    ms?: number;
  }[];
}

export interface LocalEnrollmentFederationCheckResult {
  enrollmentId: string;
  deviceId: string;
  federationKeyId: string;
  cloudSessionId: string | null;
  federationCheck: LocalEnrollmentFederationCheck;
}

export interface HealthResponse {
  status: string;
  adapter: string;
  concurrency: {
    active: number;
    queuedTotal: number;
    maxConcurrent: number;
  };
  runningWorkflows: number;
  version?: string;
  is_docker: boolean;
}

export interface WorkflowPressureTrendBucket {
  label: string;
  start: string;
  end: string;
  total: number;
  active: number;
  queued_total: number;
  max_concurrent: number;
  running_workflows: number;
  saturation_ratio: number;
}

export interface WorkflowPressureTrendResult {
  buckets: WorkflowPressureTrendBucket[];
}

export interface WorkflowAdmissionPolicyTemplate {
  id: string;
  name: string;
  description?: string;
  tags?: string[];
  owner?: string;
  rolloutState?: 'draft' | 'active' | 'archived';
  allowCodebaseOverride?: boolean;
  lockedFields?: string[];
  policy: WorkflowAdmissionPolicyRecord;
}

export interface WorkflowAdmissionPolicyRegistry {
  templates: WorkflowAdmissionPolicyTemplate[];
}

export type WorkflowAuditEventType =
  | 'admission_denied'
  | 'template_rollout_denied'
  | 'template_override_denied'
  | 'cancel'
  | 'abandon'
  | 'approve'
  | 'reject'
  | 'delete_tombstone'
  | 'workflow_deleted'
  | 'workflow_action';

export type WorkflowAuditEventFilter = 'all' | WorkflowAuditEventType;

export type WorkflowAuditDateRange = 'today' | '7d' | '30d' | 'all';

export interface WorkflowAuditPresetState {
  eventType?: WorkflowAuditEventFilter;
  actor?: string | null;
  workflowName?: string | null;
  conversationId?: string | null;
  codebaseNamePrefix?: string | null;
  dateRange?: WorkflowAuditDateRange;
}

export interface WorkflowAuditSavedPreset {
  id: string;
  name: string;
  state: WorkflowAuditPresetState;
  createdAt: string;
}

export type AuthRole = 'viewer' | 'editor' | 'admin';

export interface AuthUserProfile {
  id: string;
  name: string;
  role: AuthRole;
  orgId?: string;
  orgName?: string;
}

export interface OrganizationProfile {
  id: string;
  name: string;
  role?: AuthRole;
}

export interface AuthSessionInfo {
  id: string;
  operatorId: string;
  activeOrgId: string;
  activeOrgName: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface AuthInvite {
  id: string;
  orgId: string;
  invitedOperatorId: string;
  invitedName: string;
  role: AuthRole;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  updatedAt: string;
  token?: string;
}

export interface AuthSessionResponse {
  authenticated: boolean;
  bootstrapRequired: boolean;
  user?: AuthUserProfile | null;
  operator: AuthUserProfile | null;
  organizations: OrganizationProfile[];
}

export interface AuthUserListResponse {
  users?: AuthUserProfile[];
  operators: AuthUserProfile[];
}

export interface OrganizationListResponse {
  organizations: OrganizationProfile[];
  activeOrgId: string | null;
}

export interface AuthSessionListResponse {
  currentSessionId: string | null;
  sessions: AuthSessionInfo[];
}

export interface AuthInviteListResponse {
  invites: AuthInvite[];
}

function normalizeAuthRole(value: unknown): AuthRole {
  return value === 'admin' || value === 'editor' ? value : 'viewer';
}

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  // Prepend API_PREFIX so fetches go back through the public app mount
  // when the app is deployed behind a base path. For root deployments
  // API_PREFIX is '', so behavior is unchanged.
  const resolvedUrl =
    url.startsWith('/') && !url.startsWith(API_PREFIX) ? `${API_PREFIX}${url}` : url;
  const res = await fetch(resolvedUrl, {
    ...options,
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.text();
    // Don't truncate 422 responses — callers (e.g. AddProjectDialog) need to JSON.parse the full body
    const maxLen = res.status === 422 ? 5000 : 200;
    const truncated = body.length > maxLen ? body.slice(0, maxLen) + '...' : body;
    const path = new URL(url, window.location.origin).pathname;
    const error = new Error(`API error ${String(res.status)} (${path}): ${truncated}`);
    (error as Error & { status: number }).status = res.status;
    throw error;
  }
  return res.json() as Promise<T>;
}

export async function getAuthSession(): Promise<AuthSessionResponse> {
  const response = await fetchJSON<AuthSessionResponse>('/api/auth/session');
  return {
    ...response,
    operator: response.operator ?? response.user ?? null,
  };
}

/**
 * Platform readiness probe. Returns 200 when phase ∈ {ready, degraded}, 503
 * otherwise. Both bodies have the same shape (BootState). Callers should
 * poll this until phase settles rather than branching on HTTP status —
 * 503 still returns a parseable BootState with per-check detail.
 */
export async function getBootState(): Promise<BootState> {
  const target = `${API_PREFIX}/api/ready`;
  const res = await fetch(target, { credentials: 'include' });
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new Error('Readiness endpoint returned invalid JSON');
  }
  return parseBootStateResponse(res.status, body);
}

export async function bootstrapAuthAdmin(input: {
  id: string;
  name: string;
  orgName?: string;
  password: string;
}): Promise<AuthSessionResponse> {
  const response = await fetchJSON<AuthSessionResponse>('/api/auth/bootstrap', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return {
    ...response,
    operator: response.operator ?? response.user ?? null,
  };
}

export async function loginAuthUser(input: {
  id: string;
  password: string;
}): Promise<AuthSessionResponse> {
  const response = await fetchJSON<AuthSessionResponse>('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return {
    ...response,
    operator: response.operator ?? response.user ?? null,
  };
}

export async function logoutAuthUser(): Promise<{ success: boolean }> {
  return fetchJSON('/api/auth/logout', {
    method: 'POST',
  });
}

export async function listOrganizations(): Promise<OrganizationListResponse> {
  return fetchJSON('/api/auth/orgs');
}

export async function switchActiveOrganization(orgId: string): Promise<{
  operator: AuthUserProfile | null;
  organizations: OrganizationProfile[];
}> {
  return fetchJSON('/api/auth/switch-org', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ orgId }),
  });
}

export async function listAuthUsers(): Promise<AuthUserListResponse> {
  const response = await fetchJSON<AuthUserListResponse>('/api/auth/users');
  return {
    ...response,
    operators: response.operators ?? response.users ?? [],
  };
}

export async function upsertAuthUser(
  id: string,
  input: { name: string; role: AuthRole; password?: string }
): Promise<{ operator: AuthUserProfile | null }> {
  return fetchJSON(`/api/auth/users/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: input.name.trim(),
      role: normalizeAuthRole(input.role),
      ...(input.password ? { password: input.password } : {}),
    }),
  });
}

export async function listAuthSessions(): Promise<AuthSessionListResponse> {
  return fetchJSON('/api/auth/sessions');
}

export async function revokeAuthSession(
  sessionId: string
): Promise<{ success: boolean; revokedCurrentSession: boolean }> {
  return fetchJSON(`/api/auth/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
  });
}

export async function listOrganizationInvites(): Promise<AuthInviteListResponse> {
  return fetchJSON('/api/auth/invites');
}

export async function createOrganizationInvite(input: {
  id: string;
  name: string;
  role: AuthRole;
}): Promise<{ invite: AuthInvite | null }> {
  return fetchJSON('/api/auth/invites', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function revokeOrganizationInvite(inviteId: string): Promise<{ success: boolean }> {
  return fetchJSON(`/api/auth/invites/${encodeURIComponent(inviteId)}`, {
    method: 'DELETE',
  });
}

export async function acceptOrganizationInvite(input: {
  token: string;
  password: string;
  name?: string;
}): Promise<AuthSessionResponse> {
  const response = await fetchJSON<AuthSessionResponse>('/api/auth/invites/accept', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return {
    ...response,
    operator: response.operator ?? response.user ?? null,
  };
}

export async function requestAuthPasswordReset(
  id: string
): Promise<{ success: boolean; resetToken: string }> {
  return fetchJSON('/api/auth/password/reset-request', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id }),
  });
}

export async function resetAuthPassword(input: {
  token: string;
  password: string;
}): Promise<{ success: boolean }> {
  return fetchJSON('/api/auth/password/reset', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

// Conversations
export async function listConversations(codebaseId?: string): Promise<ConversationResponse[]> {
  const params = new URLSearchParams();
  if (codebaseId) params.set('codebaseId', codebaseId);
  const qs = params.toString();
  return fetchJSON<ConversationResponse[]>(`/api/conversations${qs ? `?${qs}` : ''}`);
}

export async function createConversation(
  codebaseId?: string,
  message?: string
): Promise<{ conversationId: string; id: string; dispatched?: boolean }> {
  const body: Record<string, string> = {};
  if (codebaseId) body.codebaseId = codebaseId;
  if (message) body.message = message;

  return fetchJSON('/api/conversations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function updateConversation(
  id: string,
  updates: { title?: string }
): Promise<{ success: boolean }> {
  return fetchJSON(`/api/conversations/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
}

export async function deleteConversation(id: string): Promise<{ success: boolean }> {
  return fetchJSON(`/api/conversations/${id}`, { method: 'DELETE' });
}

export async function sendMessage(
  conversationId: string,
  message: string,
  files?: File[]
): Promise<{ accepted: boolean; status: string }> {
  const url = `/api/conversations/${encodeURIComponent(conversationId)}/message`;

  if (!files || files.length === 0) {
    return fetchJSON(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message }),
    });
  }

  const form = new FormData();
  form.append('message', message);
  for (const file of files) {
    form.append('files', file, file.name);
  }
  // No Content-Type header — browser sets multipart/form-data with boundary automatically
  return fetchJSON(url, { method: 'POST', body: form });
}

// Messages
export interface MessageResponse {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant';
  content: string;
  metadata: string;
  created_at: string;
}

export async function getMessages(conversationId: string, limit = 200): Promise<MessageResponse[]> {
  return fetchJSON<MessageResponse[]>(
    `/api/conversations/${encodeURIComponent(conversationId)}/messages?limit=${String(limit)}`
  );
}

// Codebases
export async function listCodebases(): Promise<CodebaseResponse[]> {
  return fetchJSON<CodebaseResponse[]>('/api/codebases');
}

export async function listWorkRequests(): Promise<WorkRequestResponse[]> {
  return fetchJSON<WorkRequestResponse[]>('/api/work-requests');
}

export async function createWorkRequest(
  input: CreateWorkRequestInput
): Promise<WorkRequestResponse> {
  return fetchJSON<WorkRequestResponse>('/api/work-requests', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function patchWorkRequest(
  id: string,
  input: Partial<CreateWorkRequestInput> & { state?: WorkRequestState; deliveryNote?: string }
): Promise<WorkRequestResponse> {
  return fetchJSON<WorkRequestResponse>(`/api/work-requests/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function dispatchWorkRequest(
  id: string,
  message: string
): Promise<{ accepted: boolean; status: string; conversationId: string }> {
  return fetchJSON(`/api/work-requests/${encodeURIComponent(id)}/dispatch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message }),
  });
}

export async function syncWorkRequestGitea(id: string): Promise<WorkRequestResponse> {
  return fetchJSON<WorkRequestResponse>(`/api/work-requests/${encodeURIComponent(id)}/sync-gitea`, {
    method: 'POST',
  });
}

export async function createWorkRequestOutcome(id: string): Promise<WorkRequestResponse> {
  return fetchJSON<WorkRequestResponse>(
    `/api/work-requests/${encodeURIComponent(id)}/create-outcome`,
    { method: 'POST' }
  );
}

// Change-approval gate (P1 slice 2) — web is the primary approve/reject surface.
export async function requestWorkRequestApproval(id: string): Promise<WorkRequestResponse> {
  return fetchJSON<WorkRequestResponse>(
    `/api/work-requests/${encodeURIComponent(id)}/request-approval`,
    { method: 'POST' }
  );
}

export async function approveWorkRequest(id: string, note?: string): Promise<WorkRequestResponse> {
  return fetchJSON<WorkRequestResponse>(`/api/work-requests/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note }),
  });
}

export async function rejectWorkRequest(id: string, reason?: string): Promise<WorkRequestResponse> {
  return fetchJSON<WorkRequestResponse>(`/api/work-requests/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

// Change-deploy wiring (P1 slice 3).
export interface WorkRequestDeployDispatch {
  conversationId: string;
  workflowName: string;
  outcomeId: string;
}

export interface WorkRequestDeployment {
  outcomeId: string;
  environment: string;
  manifestId: string;
  status: string;
  deployedAt: string;
  deploymentMode: string;
  targetName: string;
  artifactUri: string;
  previousManifestId: string | null;
}

export async function deployWorkRequest(id: string): Promise<WorkRequestDeployDispatch> {
  return fetchJSON<WorkRequestDeployDispatch>(
    `/api/work-requests/${encodeURIComponent(id)}/deploy`,
    { method: 'POST' }
  );
}

export async function getWorkRequestDeployment(id: string): Promise<WorkRequestDeployment[]> {
  return fetchJSON<WorkRequestDeployment[]>(
    `/api/work-requests/${encodeURIComponent(id)}/deployment`
  );
}

// Change validate-and-close (P1 slice 4).
export async function validateWorkRequest(id: string, note: string): Promise<WorkRequestResponse> {
  return fetchJSON<WorkRequestResponse>(`/api/work-requests/${encodeURIComponent(id)}/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note }),
  });
}

// Change-request rollback loop (P1 slice 5) — resolve target + record intent.
export interface WorkRequestRollbackTarget {
  outcomeId: string;
  environment: string;
  currentManifestId: string;
  targetManifestId: string;
  artifactUri: string;
  artifactSha256: string;
  commitSha?: string;
  command: string;
}

export async function getWorkRequestRollbackTargets(
  id: string
): Promise<WorkRequestRollbackTarget[]> {
  return fetchJSON<WorkRequestRollbackTarget[]>(
    `/api/work-requests/${encodeURIComponent(id)}/rollback-target`
  );
}

export async function requestWorkRequestRollback(
  id: string,
  environment: string,
  note?: string
): Promise<WorkRequestRollbackTarget> {
  return fetchJSON<WorkRequestRollbackTarget>(
    `/api/work-requests/${encodeURIComponent(id)}/request-rollback`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ environment, note }),
    }
  );
}

// Execute-rollback (PR2) — gated, dry-run by default.
export interface WorkRequestRollbackExecution {
  dryRun: boolean;
  commands?: string[];
  exitCode?: number;
  manifestId?: string;
}

export async function executeWorkRequestRollback(
  id: string,
  opts: { dryRun?: boolean; confirm?: boolean } = {}
): Promise<WorkRequestRollbackExecution> {
  return fetchJSON<WorkRequestRollbackExecution>(
    `/api/work-requests/${encodeURIComponent(id)}/execute-rollback`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(opts),
    }
  );
}

export async function prepareLocalEnrollment(
  input: PrepareLocalEnrollmentInput
): Promise<PrepareLocalEnrollmentResult> {
  return fetchJSON<PrepareLocalEnrollmentResult>('/api/local-enrollments/prepare', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function approveLocalEnrollment(
  id: string,
  manifestHash: string
): Promise<ApproveLocalEnrollmentResult> {
  return fetchJSON<ApproveLocalEnrollmentResult>(
    `/api/local-enrollments/${encodeURIComponent(id)}/approve`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ manifestHash }),
    }
  );
}

export async function requestLocalEnrollmentAuthorityProvisioning(
  id: string
): Promise<LocalEnrollmentAuthorityStateResult> {
  return fetchJSON<LocalEnrollmentAuthorityStateResult>(
    `/api/local-enrollments/${encodeURIComponent(id)}/request-authority-provisioning`,
    { method: 'POST' }
  );
}

export async function fetchLocalEnrollmentAuthorityStatus(
  id: string
): Promise<LocalEnrollmentAuthorityStateResult> {
  return fetchJSON<LocalEnrollmentAuthorityStateResult>(
    `/api/local-enrollments/${encodeURIComponent(id)}/authority-status`
  );
}

export async function approveLocalEnrollmentSecretRelease(
  id: string
): Promise<LocalEnrollmentAuthorityStateResult> {
  return fetchJSON<LocalEnrollmentAuthorityStateResult>(
    `/api/local-enrollments/${encodeURIComponent(id)}/approve-secret-release`,
    { method: 'POST' }
  );
}

export async function approveLocalEnrollmentActivation(
  id: string
): Promise<LocalEnrollmentAuthorityStateResult> {
  return fetchJSON<LocalEnrollmentAuthorityStateResult>(
    `/api/local-enrollments/${encodeURIComponent(id)}/approve-activation`,
    { method: 'POST' }
  );
}

export async function issueLocalEnrollmentToken(id: string): Promise<LocalEnrollmentTokenResult> {
  return fetchJSON<LocalEnrollmentTokenResult>(
    `/api/local-enrollments/${encodeURIComponent(id)}/issue-token`,
    { method: 'POST' }
  );
}

export async function reissueLocalEnrollmentToken(id: string): Promise<LocalEnrollmentTokenResult> {
  return fetchJSON<LocalEnrollmentTokenResult>(
    `/api/local-enrollments/${encodeURIComponent(id)}/reissue-token`,
    { method: 'POST' }
  );
}

export async function launchLocalEnrollmentDocker(
  id: string,
  input: { token: string }
): Promise<LocalEnrollmentDockerLaunchResult> {
  return fetchJSON<LocalEnrollmentDockerLaunchResult>(
    `/api/local-enrollments/${encodeURIComponent(id)}/launch/docker`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }
  );
}

export async function runLocalEnrollmentDoctorCheck(
  id: string
): Promise<LocalEnrollmentDoctorCheckResult> {
  return fetchJSON<LocalEnrollmentDoctorCheckResult>(
    `/api/local-enrollments/${encodeURIComponent(id)}/doctor`
  );
}

export async function runLocalEnrollmentFederationCheck(
  id: string
): Promise<LocalEnrollmentFederationCheckResult> {
  return fetchJSON<LocalEnrollmentFederationCheckResult>(
    `/api/local-enrollments/${encodeURIComponent(id)}/federation-check`
  );
}

// Operator approves a device-authorization request the paired:enroll CLI started.
// Session-cookie authed (fetchJSON sends credentials); the server issues the token
// and hands it to the polling CLI. Nothing redeemable is returned to the browser.
export async function approveEnrollmentDeviceAuth(userCode: string): Promise<{ ok: boolean }> {
  return fetchJSON<{ ok: boolean }>('/api/local-enrollments/device-auth/approve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userCode }),
  });
}

export interface LocalEnrollmentListItem {
  id: string;
  deviceId: string;
  status: string;
  createdAt: string;
}

export async function listLocalEnrollments(): Promise<LocalEnrollmentListItem[]> {
  const res = await fetchJSON<{ enrollments: LocalEnrollmentListItem[] }>(
    '/api/local-enrollments',
    { method: 'GET' }
  );
  return res.enrollments;
}

export async function fetchLiveDeployment(outcomeId: string): Promise<LiveDeploymentSnapshot> {
  return fetchJSON<LiveDeploymentSnapshot>(
    `/api/outcomes/${encodeURIComponent(outcomeId)}/live-deployment`
  );
}

export async function recordLiveDeploymentStepAttempt(
  outcomeId: string,
  stepId: string,
  input: LiveDeploymentAttemptInput
): Promise<{ step: LiveDeploymentStep }> {
  return fetchJSON<{ step: LiveDeploymentStep }>(
    `/api/outcomes/${encodeURIComponent(outcomeId)}/live-deployment/steps/${encodeURIComponent(
      stepId
    )}/attempts`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }
  );
}

export async function recordLiveDeploymentEvidence(
  outcomeId: string,
  evidenceId: string,
  content: string
): Promise<LiveDeploymentSnapshot> {
  return fetchJSON<LiveDeploymentSnapshot>(
    `/api/outcomes/${encodeURIComponent(outcomeId)}/live-deployment/evidence/${encodeURIComponent(
      evidenceId
    )}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    }
  );
}

export async function completeLiveDeployment(
  outcomeId: string
): Promise<LiveDeploymentCompletionResult> {
  return fetchJSON<LiveDeploymentCompletionResult>(
    `/api/outcomes/${encodeURIComponent(outcomeId)}/live-deployment/complete`,
    { method: 'POST' }
  );
}

export async function getCodebase(id: string): Promise<CodebaseResponse> {
  return fetchJSON<CodebaseResponse>(`/api/codebases/${id}`);
}

export interface DetectedEnvKey {
  name: string;
  file: string;
}

export interface AddCodebaseResult extends CodebaseResponse {
  migratedKeys?: { file: string; key: string }[];
  /** Operator/infra keys detected but left in .env (route to MOS Vault, not the project store). */
  operatorKeys?: { file: string; key: string }[];
}

export interface EnvLeakResponse {
  error: string;
  detectedKeys: DetectedEnvKey[];
  canMigrate: boolean;
}

export type AddCodebaseInput =
  | {
      url: string;
      projectSource?: ProjectSourceRecord;
      createProject?: boolean;
      allowEnvKeys?: boolean;
      migrateKeys?: boolean;
    }
  | {
      path: string;
      projectSource?: ProjectSourceRecord;
      createProject?: boolean;
      allowEnvKeys?: boolean;
      migrateKeys?: boolean;
    };

export async function addCodebase(input: AddCodebaseInput): Promise<AddCodebaseResult> {
  return fetchJSON<AddCodebaseResult>('/api/codebases', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function listProjectSources(): Promise<{ sources: ProjectSourceCatalogEntry[] }> {
  return fetchJSON<{ sources: ProjectSourceCatalogEntry[] }>('/api/project-sources');
}

export interface ProjectSourceMutationInput {
  id?: string;
  label?: string;
  description?: string;
  kind?: ProjectSourceCatalogEntry['kind'];
  provider?: ProjectSourceCatalogEntry['provider'];
  baselineId?: string | null;
  repositoryUrl?: string | null;
  localMirrorPath?: string | null;
  ref?: string | null;
  visibility?: ProjectSourceCatalogEntry['visibility'];
  recommended?: boolean;
  sortOrder?: number;
  status?: ProjectSourceCatalogEntry['status'];
}

export async function createProjectSource(
  input: ProjectSourceMutationInput
): Promise<{ source: ProjectSourceCatalogEntry }> {
  return fetchJSON<{ source: ProjectSourceCatalogEntry }>('/api/project-sources', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function updateProjectSource(
  id: string,
  input: ProjectSourceMutationInput
): Promise<{ source: ProjectSourceCatalogEntry }> {
  return fetchJSON<{ source: ProjectSourceCatalogEntry }>(`/api/project-sources/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function archiveProjectSource(
  id: string
): Promise<{ source: ProjectSourceCatalogEntry }> {
  return fetchJSON<{ source: ProjectSourceCatalogEntry }>(`/api/project-sources/${id}`, {
    method: 'DELETE',
  });
}

export async function updateCodebase(
  id: string,
  input: {
    allowEnvKeys?: boolean;
    workflowAdmissionPolicy?: WorkflowAdmissionPolicyRecord | null;
    workflowAuditPresets?: WorkflowAuditSavedPreset[] | null;
  }
): Promise<CodebaseResponse> {
  return fetchJSON<CodebaseResponse>(`/api/codebases/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function deleteCodebase(id: string): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(`/api/codebases/${id}`, { method: 'DELETE' });
}

export interface WorkflowRunResponse {
  id: string;
  workflow_name: string;
  conversation_id: string;
  parent_conversation_id: string | null;
  codebase_id: string | null;
  working_path: string | null;
  status: WorkflowRunStatus;
  user_message: string;
  metadata: Record<string, unknown>;
  started_at: string;
  completed_at: string | null;
  last_activity_at: string | null;
  worker_platform_id?: string;
  parent_platform_id?: string;
  conversation_platform_id?: string;
}

export interface WorkflowEventResponse {
  id: string;
  workflow_run_id: string;
  event_type: string;
  step_index: number | null;
  step_name: string | null;
  data: Record<string, unknown>;
  created_at: string;
}

export type WorkflowListEntry = components['schemas']['WorkflowListEntry'];

export async function listWorkflows(cwd?: string): Promise<WorkflowListEntry[]> {
  const params = cwd ? `?cwd=${encodeURIComponent(cwd)}` : '';
  const result = await fetchJSON<{ workflows: WorkflowListEntry[] }>(`/api/workflows${params}`);
  return result.workflows;
}

export async function runWorkflow(
  name: string,
  conversationId: string,
  message: string
): Promise<{ accepted: boolean; status: string }> {
  return fetchJSON(`/api/workflows/${encodeURIComponent(name)}/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ conversationId, message }),
  });
}

// Dashboard-enriched workflow run (includes joined data from single SQL query)
export interface DashboardRunResponse extends Omit<
  WorkflowRunResponse,
  'worker_platform_id' | 'parent_platform_id'
> {
  working_path: string | null;
  codebase_name: string | null;
  platform_type: string | null;
  worker_platform_id: string | null;
  parent_platform_id: string | null;
  current_step_name: string | null;
  total_steps: number | null;
  current_step_status: 'running' | 'completed' | 'failed' | null;
  agents_completed: number | null;
  agents_failed: number | null;
  agents_total: number | null;
}

/** Status counts across all matching runs (ignoring status filter). */
export interface DashboardCounts {
  all: number;
  running: number;
  completed: number;
  failed: number;
  cancelled: number;
  pending: number;
  paused: number;
}

/** Paginated dashboard runs response. */
export interface DashboardRunsResult {
  runs: DashboardRunResponse[];
  total: number;
  counts: DashboardCounts;
}

export interface WorkflowAuditEntryResponse {
  id: string;
  workflow_run_id: string | null;
  workflow_name: string | null;
  conversation_id: string | null;
  codebase_id: string | null;
  codebase_name: string | null;
  actor?: string | null;
  event_type: WorkflowAuditEventType;
  reason: string;
  created_at: string;
  data: Record<string, unknown>;
}

export interface WorkflowAuditFeedResult {
  entries: WorkflowAuditEntryResponse[];
  total: number;
}

export interface WorkflowAuditExportResult {
  entries: WorkflowAuditEntryResponse[];
  total: number;
  exported_at: string;
  filename: string;
}

export interface WorkflowAuditTrendBucketResponse {
  label: string;
  start: string;
  end: string;
  total: number;
  denied: number;
  denial_rate: number;
  policy_source_counts: Record<string, number>;
}

export interface WorkflowAuditTrendResult {
  buckets: WorkflowAuditTrendBucketResponse[];
}

export interface WorkflowAuditCleanupResult {
  deleted_count: number;
  retention_days: number | null;
  cutoff_before?: string | null;
}

export async function listWorkflowAuditEntries(options?: {
  codebaseId?: string;
  codebaseIds?: string[];
  codebaseName?: string;
  codebaseNamePrefix?: string;
  workflowName?: string;
  conversationId?: string;
  eventType?: WorkflowAuditEventType;
  actor?: string;
  search?: string;
  after?: string;
  before?: string;
  limit?: number;
  offset?: number;
}): Promise<WorkflowAuditFeedResult> {
  const params = new URLSearchParams();
  if (options?.codebaseId) params.set('codebaseId', options.codebaseId);
  if (options?.codebaseIds?.length) params.set('codebaseIds', options.codebaseIds.join(','));
  if (options?.codebaseName) params.set('codebaseName', options.codebaseName);
  if (options?.codebaseNamePrefix) params.set('codebaseNamePrefix', options.codebaseNamePrefix);
  if (options?.workflowName) params.set('workflowName', options.workflowName);
  if (options?.conversationId) params.set('conversationId', options.conversationId);
  if (options?.eventType) params.set('eventType', options.eventType);
  if (options?.actor) params.set('actor', options.actor);
  if (options?.search) params.set('search', options.search);
  if (options?.after) params.set('after', options.after);
  if (options?.before) params.set('before', options.before);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  return fetchJSON<WorkflowAuditFeedResult>(`/api/dashboard/workflow-audit${qs ? `?${qs}` : ''}`);
}

export async function exportWorkflowAuditEntries(options?: {
  codebaseId?: string;
  codebaseIds?: string[];
  codebaseName?: string;
  codebaseNamePrefix?: string;
  workflowName?: string;
  conversationId?: string;
  eventType?: WorkflowAuditEventType;
  actor?: string;
  search?: string;
  after?: string;
  before?: string;
  limit?: number;
  offset?: number;
}): Promise<WorkflowAuditExportResult> {
  const params = new URLSearchParams();
  if (options?.codebaseId) params.set('codebaseId', options.codebaseId);
  if (options?.codebaseIds?.length) params.set('codebaseIds', options.codebaseIds.join(','));
  if (options?.codebaseName) params.set('codebaseName', options.codebaseName);
  if (options?.codebaseNamePrefix) params.set('codebaseNamePrefix', options.codebaseNamePrefix);
  if (options?.workflowName) params.set('workflowName', options.workflowName);
  if (options?.conversationId) params.set('conversationId', options.conversationId);
  if (options?.eventType) params.set('eventType', options.eventType);
  if (options?.actor) params.set('actor', options.actor);
  if (options?.search) params.set('search', options.search);
  if (options?.after) params.set('after', options.after);
  if (options?.before) params.set('before', options.before);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  return fetchJSON<WorkflowAuditExportResult>(
    `/api/dashboard/workflow-audit/export${qs ? `?${qs}` : ''}`
  );
}

export async function listWorkflowAuditTrends(options?: {
  codebaseId?: string;
  codebaseIds?: string[];
  codebaseName?: string;
  codebaseNamePrefix?: string;
  workflowName?: string;
  conversationId?: string;
  eventType?: WorkflowAuditEventType;
  actor?: string;
  search?: string;
  after?: string;
  before?: string;
  bucketCount?: number;
}): Promise<WorkflowAuditTrendResult> {
  const params = new URLSearchParams();
  if (options?.codebaseId) params.set('codebaseId', options.codebaseId);
  if (options?.codebaseIds?.length) params.set('codebaseIds', options.codebaseIds.join(','));
  if (options?.codebaseName) params.set('codebaseName', options.codebaseName);
  if (options?.codebaseNamePrefix) params.set('codebaseNamePrefix', options.codebaseNamePrefix);
  if (options?.workflowName) params.set('workflowName', options.workflowName);
  if (options?.conversationId) params.set('conversationId', options.conversationId);
  if (options?.eventType) params.set('eventType', options.eventType);
  if (options?.actor) params.set('actor', options.actor);
  if (options?.search) params.set('search', options.search);
  if (options?.after) params.set('after', options.after);
  if (options?.before) params.set('before', options.before);
  if (options?.bucketCount) params.set('bucketCount', String(options.bucketCount));
  const qs = params.toString();
  return fetchJSON<WorkflowAuditTrendResult>(
    `/api/dashboard/workflow-audit/trends${qs ? `?${qs}` : ''}`
  );
}

export async function listWorkflowPressureTrends(options?: {
  after?: string;
  before?: string;
  bucketCount?: number;
}): Promise<WorkflowPressureTrendResult> {
  const params = new URLSearchParams();
  if (options?.after) params.set('after', options.after);
  if (options?.before) params.set('before', options.before);
  if (options?.bucketCount) params.set('bucketCount', String(options.bucketCount));
  const qs = params.toString();
  return fetchJSON<WorkflowPressureTrendResult>(
    `/api/dashboard/workflow-pressure/trends${qs ? `?${qs}` : ''}`
  );
}

export async function cleanupWorkflowAuditEntries(options: {
  retentionDays?: number;
  before?: string;
  codebaseId?: string;
  codebaseIds?: string[];
  codebaseName?: string;
  codebaseNamePrefix?: string;
  workflowName?: string;
  conversationId?: string;
  eventType?: WorkflowAuditEventType;
  actor?: string;
}): Promise<WorkflowAuditCleanupResult> {
  return fetchJSON<WorkflowAuditCleanupResult>('/api/dashboard/workflow-audit/cleanup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(options),
  });
}

export async function listDashboardRuns(options?: {
  status?: WorkflowRunStatus;
  codebaseId?: string;
  search?: string;
  after?: string;
  before?: string;
  limit?: number;
  offset?: number;
}): Promise<DashboardRunsResult> {
  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  if (options?.codebaseId) params.set('codebaseId', options.codebaseId);
  if (options?.search) params.set('search', options.search);
  if (options?.after) params.set('after', options.after);
  if (options?.before) params.set('before', options.before);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  return fetchJSON<DashboardRunsResult>(`/api/dashboard/runs${qs ? `?${qs}` : ''}`);
}

export async function cancelWorkflowRun(
  runId: string
): Promise<{ success: boolean; message: string }> {
  return fetchJSON(`/api/workflows/runs/${encodeURIComponent(runId)}/cancel`, {
    method: 'POST',
  });
}

export async function resumeWorkflowRun(
  runId: string
): Promise<{ success: boolean; message: string }> {
  return fetchJSON(`/api/workflows/runs/${encodeURIComponent(runId)}/resume`, {
    method: 'POST',
  });
}

export async function abandonWorkflowRun(
  runId: string
): Promise<{ success: boolean; message: string }> {
  return fetchJSON(`/api/workflows/runs/${encodeURIComponent(runId)}/abandon`, {
    method: 'POST',
  });
}

export async function deleteWorkflowRun(
  runId: string
): Promise<{ success: boolean; message: string }> {
  return fetchJSON(`/api/workflows/runs/${encodeURIComponent(runId)}`, {
    method: 'DELETE',
  });
}

export async function approveWorkflowRun(
  runId: string,
  comment?: string
): Promise<{ success: boolean; message: string }> {
  return fetchJSON(`/api/workflows/runs/${encodeURIComponent(runId)}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ comment }),
  });
}

export async function rejectWorkflowRun(
  runId: string,
  reason?: string
): Promise<{ success: boolean; message: string }> {
  return fetchJSON(`/api/workflows/runs/${encodeURIComponent(runId)}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

export async function listWorkflowRuns(options?: {
  conversationId?: string;
  status?: WorkflowRunStatus;
  limit?: number;
  codebaseId?: string;
  workflowName?: string;
  workingPath?: string;
}): Promise<WorkflowRunResponse[]> {
  const params = new URLSearchParams();
  if (options?.conversationId) params.set('conversationId', options.conversationId);
  if (options?.status) params.set('status', options.status);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.codebaseId) params.set('codebaseId', options.codebaseId);
  if (options?.workflowName) params.set('workflowName', options.workflowName);
  if (options?.workingPath) params.set('workingPath', options.workingPath);
  const qs = params.toString();
  const result = await fetchJSON<{ runs: WorkflowRunResponse[] }>(
    `/api/workflows/runs${qs ? `?${qs}` : ''}`
  );
  return result.runs;
}

export async function getWorkflowRun(
  runId: string
): Promise<{ run: WorkflowRunResponse; events: WorkflowEventResponse[] }> {
  return fetchJSON(`/api/workflows/runs/${encodeURIComponent(runId)}`);
}

export async function getWorkflowRunByWorker(
  workerPlatformId: string
): Promise<{ run: WorkflowRunResponse } | null> {
  try {
    return await fetchJSON(`/api/workflows/runs/by-worker/${encodeURIComponent(workerPlatformId)}`);
  } catch (e: unknown) {
    // 404 means no run exists yet — expected during dispatch
    if ((e as Error & { status?: number }).status === 404) {
      return null;
    }
    throw e;
  }
}

export type WorkflowSource = components['schemas']['WorkflowSource'];

export interface GetWorkflowResponse {
  workflow: WorkflowDefinition;
  filename: string;
  source: WorkflowSource;
}

export async function getWorkflow(name: string, cwd?: string): Promise<GetWorkflowResponse> {
  const params = cwd ? `?cwd=${encodeURIComponent(cwd)}` : '';
  return fetchJSON(`/api/workflows/${encodeURIComponent(name)}${params}`);
}

export async function saveWorkflow(
  name: string,
  definition: WorkflowDefinition,
  cwd?: string
): Promise<GetWorkflowResponse> {
  const params = cwd ? `?cwd=${encodeURIComponent(cwd)}` : '';
  return fetchJSON(`/api/workflows/${encodeURIComponent(name)}${params}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ definition }),
  });
}

export async function deleteWorkflow(
  name: string,
  cwd?: string
): Promise<{ deleted: boolean; name: string }> {
  const params = cwd ? `?cwd=${encodeURIComponent(cwd)}` : '';
  return fetchJSON(`/api/workflows/${encodeURIComponent(name)}${params}`, {
    method: 'DELETE',
  });
}

export async function validateWorkflow(
  definition: WorkflowDefinition
): Promise<{ valid: boolean; errors?: string[] }> {
  return fetchJSON('/api/workflows/validate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ definition }),
  });
}

export interface CommandEntry {
  name: string;
  source: WorkflowSource;
}

export async function listCommands(cwd?: string): Promise<CommandEntry[]> {
  const params = cwd ? `?cwd=${encodeURIComponent(cwd)}` : '';
  const result = await fetchJSON<{ commands: CommandEntry[] }>(`/api/commands${params}`);
  return result.commands;
}

export type SafeConfigResponse = components['schemas']['SafeConfig'] & {
  workflowAdmissionRegistry?: WorkflowAdmissionPolicyRegistry;
  workflowAuditPresets?: WorkflowAuditSavedPreset[];
};

export async function getConfig(): Promise<{ config: SafeConfigResponse; database: string }> {
  return fetchJSON('/api/config');
}

export type UpdateAssistantConfigBody = components['schemas']['UpdateAssistantConfigBody'];

export async function updateAssistantConfig(
  body: UpdateAssistantConfigBody
): Promise<{ config: SafeConfigResponse; database: string }> {
  return fetchJSON('/api/config/assistants', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function updateWorkflowAdmissionRegistry(
  registry: WorkflowAdmissionPolicyRegistry
): Promise<{ config: SafeConfigResponse; database: string }> {
  return fetchJSON('/api/config/workflow-admission-registry', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(registry),
  });
}

export async function updateWorkflowAuditPresets(
  presets: WorkflowAuditSavedPreset[]
): Promise<{ config: SafeConfigResponse; database: string }> {
  return fetchJSON('/api/config/workflow-audit-presets', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ presets }),
  });
}

export type IsolationEnvironment = components['schemas']['IsolationEnvironment'];

export async function getCodebaseEnvironments(codebaseId: string): Promise<IsolationEnvironment[]> {
  const result = await fetchJSON<{ environments: IsolationEnvironment[] }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/environments`
  );
  return result.environments;
}

// Codebase env vars
export async function getCodebaseEnvVars(codebaseId: string): Promise<string[]> {
  const result = await fetchJSON<{ keys: string[] }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/env`
  );
  return result.keys;
}

export async function setCodebaseEnvVar(
  codebaseId: string,
  data: { key: string; value: string }
): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(`/api/codebases/${encodeURIComponent(codebaseId)}/env`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteCodebaseEnvVar(
  codebaseId: string,
  key: string
): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/env/${encodeURIComponent(key)}`,
    { method: 'DELETE' }
  );
}

// Secure env vault
export async function listSecureKeys(codebaseId: string): Promise<string[]> {
  const result = await fetchJSON<{ keys: string[] }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/secure-env`
  );
  return result.keys;
}

export async function setSecureKey(
  codebaseId: string,
  data: { key: string; value: string }
): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/secure-env`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  );
}

export async function deleteSecureKey(
  codebaseId: string,
  key: string
): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/secure-env/${encodeURIComponent(key)}`,
    { method: 'DELETE' }
  );
}

export interface MigrateKeysResult {
  movedKeys: { file: string; key: string }[];
  failedKeys: { file: string; key: string; reason: string }[];
  /** Operator/infra keys detected but left in .env (route to MOS Vault, not the project store). */
  operatorKeys: { file: string; key: string }[];
}

export async function migrateKeysToVault(codebaseId: string): Promise<MigrateKeysResult> {
  return fetchJSON<MigrateKeysResult>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/secure-env/migrate`,
    { method: 'POST' }
  );
}

export interface SecureStorageConfig {
  path: string;
  writable: boolean;
}

export async function getSecureStorageConfig(): Promise<SecureStorageConfig> {
  return fetchJSON<SecureStorageConfig>('/api/secure-storage/config');
}

export interface DatabaseValidationSummaryResponse {
  generatedAt: string;
  governance: {
    artifacts: { total: number; active: number; drifted: number };
    validationRules: { total: number; active: number };
    sampleArtifacts: {
      id: string;
      title: string;
      artifactType: string;
      status: string;
      driftStatus: string;
      canonicalPath: string | null;
    }[];
    sampleRules: {
      id: string;
      title: string;
      severity: string;
      status: string;
      appliesTo: string[];
    }[];
  };
  deployment: {
    profiles: { total: number };
    manifests: { total: number; required: number };
    sampleProfiles: {
      id: string;
      displayName: string;
      environmentType: string;
      status: string;
    }[];
    sampleManifests: {
      id: string;
      manifestKey: string;
      targetArea: string;
      version: string;
      isRequired: boolean;
      status: string;
    }[];
  };
  learning: {
    events: { total: number };
    lessons: { total: number };
    bestPractices: { total: number };
    driftFindings: { total: number; open: number };
    recentEvents: {
      id: string;
      eventType: string;
      domain: string;
      summary: string;
      status: string;
      createdAt: string;
    }[];
    recentDriftFindings: {
      id: string;
      findingType: string;
      severity: string;
      summary: string;
      status: string;
      createdAt: string;
    }[];
  };
}

export interface DatabaseValidationIssueResponse {
  id: string;
  domain: 'governance' | 'deployment' | 'learning';
  severity: 'info' | 'warn' | 'error';
  title: string;
  summary: string;
  detail: string | null;
  status: string;
  recordType: string;
  recordId: string;
  recordLabel: string;
  sourcePath: string | null;
  contextTab: 'governance' | 'deployment' | 'learning';
}

export interface DatabaseValidationIssuesResponse {
  generatedAt: string;
  totals: {
    total: number;
    error: number;
    warn: number;
    info: number;
  };
  issues: DatabaseValidationIssueResponse[];
}

export async function getDatabaseValidationSummary(): Promise<DatabaseValidationSummaryResponse> {
  return fetchJSON<DatabaseValidationSummaryResponse>('/api/database-validation/summary');
}

export async function getDatabaseValidationIssues(): Promise<DatabaseValidationIssuesResponse> {
  return fetchJSON<DatabaseValidationIssuesResponse>('/api/database-validation/issues');
}

// Filesystem browsing
export interface FilesystemEntry {
  name: string;
  path: string;
  isDirectory: boolean;
  isGitRepo: boolean;
}

export interface FilesystemListResponse {
  entries: FilesystemEntry[];
  parent: string | null;
}

export async function listFilesystem(path: string): Promise<FilesystemListResponse> {
  return fetchJSON<FilesystemListResponse>(`/api/filesystem/list?path=${encodeURIComponent(path)}`);
}

// Batch registration
export async function addCodebaseBatch(
  paths: string[],
  migrateKeys?: boolean
): Promise<{ batchId: string; total: number }> {
  return fetchJSON<{ batchId: string; total: number }>('/api/codebases/batch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ paths, migrateKeys }),
  });
}

// Outcomes
export interface AuditEntry {
  timestamp: string;
  action: string;
  actor: string;
  details?: string;
}

export interface OutcomeSummary {
  id: string;
  title: string;
  status: string;
  convergenceScore: number | null;
  convergenceAssessment?: OutcomeConvergenceAssessment;
  convergenceAssessmentAvailability?: 'available' | 'missing' | 'invalid';
  convergenceAssessmentError?: string;
  dependsOn?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface OutcomeDetail extends OutcomeSummary {
  artifacts: Record<string, string>;
  auditTrail: AuditEntry[];
}

export type ManualConvergenceAssessmentStatus = 'pending' | 'completed' | 'reviewed';

export interface OutcomeConvergenceAssessment {
  version: 1;
  score: number;
  breakdown?: {
    sourceIndependence: number;
    evidenceQuality: number;
    riskFlags: number;
    manual: number;
  };
  maxAutomated?: number;
  manualAssessment: {
    status: ManualConvergenceAssessmentStatus;
    points: number | null;
  };
  evidence: {
    testResults: TestResultInput[];
    criteriaAddressed: number;
    totalCriteria: number;
    links: string[];
  };
  source: 'automated' | 'manual' | 'known-score';
  updatedAt: string;
}

export async function listOutcomes(codebaseId: string): Promise<OutcomeSummary[]> {
  const result = await fetchJSON<{ outcomes: OutcomeSummary[] }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes`
  );
  return result.outcomes;
}

export async function createOutcome(
  codebaseId: string,
  title: string
): Promise<{ outcomeId: string }> {
  return fetchJSON<{ outcomeId: string }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    }
  );
}

export async function getOutcomeDetail(
  codebaseId: string,
  outcomeId: string
): Promise<OutcomeDetail> {
  return fetchJSON<OutcomeDetail>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes/${encodeURIComponent(outcomeId)}`
  );
}

export async function updateOutcomeStatus(
  codebaseId: string,
  outcomeId: string,
  status: string
): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes/${encodeURIComponent(outcomeId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    }
  );
}

export async function updateOutcomeTitle(
  codebaseId: string,
  outcomeId: string,
  title: string
): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes/${encodeURIComponent(outcomeId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    }
  );
}

export async function deleteOutcome(
  codebaseId: string,
  outcomeId: string
): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes/${encodeURIComponent(outcomeId)}`,
    { method: 'DELETE' }
  );
}

export async function updateOutcomeDependencies(
  codebaseId: string,
  outcomeId: string,
  dependsOn: string[]
): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes/${encodeURIComponent(outcomeId)}/dependencies`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dependsOn }),
    }
  );
}

export async function getOutcomeAudit(
  codebaseId: string,
  outcomeId: string
): Promise<AuditEntry[]> {
  const result = await fetchJSON<{ entries: AuditEntry[] }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes/${encodeURIComponent(outcomeId)}/audit`
  );
  return result.entries;
}

export async function updateOutcomeConvergenceScore(
  codebaseId: string,
  outcomeId: string,
  manualPoints: number,
  manualAssessmentStatus: Exclude<ManualConvergenceAssessmentStatus, 'pending'> = 'completed'
): Promise<{ success: boolean; score: number }> {
  return fetchJSON<{ success: boolean; score: number }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes/${encodeURIComponent(outcomeId)}/convergence`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ manualPoints, manualAssessmentStatus }),
    }
  );
}

export async function saveOutcomeArtifact(
  codebaseId: string,
  outcomeId: string,
  filename: string,
  content: string
): Promise<{ saved: boolean; filename: string }> {
  return fetchJSON<{ saved: boolean; filename: string }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes/${encodeURIComponent(outcomeId)}/artifacts`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename, content }),
    }
  );
}

export async function discoverOutcomes(
  codebaseId: string,
  sources?: string[]
): Promise<{ conversationId: string; id: string; message: string }> {
  return fetchJSON<{ conversationId: string; id: string; message: string }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes/discover`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sources }),
    }
  );
}

export async function createOutcomeConversation(
  codebaseId: string,
  outcomeId: string
): Promise<{ conversationId: string; id?: string; created: boolean }> {
  return fetchJSON<{ conversationId: string; id?: string; created: boolean }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes/${encodeURIComponent(outcomeId)}/conversation`,
    { method: 'POST' }
  );
}

export async function getOutcomeConversation(
  codebaseId: string,
  outcomeId: string
): Promise<{ conversationId: string | null }> {
  return fetchJSON<{ conversationId: string | null }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes/${encodeURIComponent(outcomeId)}/conversation`
  );
}

export async function generateOutcomeTests(
  codebaseId: string,
  outcomeId: string
): Promise<{ conversationId: string; id: string; message: string }> {
  return fetchJSON<{ conversationId: string; id: string; message: string }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes/${encodeURIComponent(outcomeId)}/generate-tests`,
    { method: 'POST' }
  );
}

export async function executeOutcome(
  codebaseId: string,
  outcomeId: string,
  workflow?: string
): Promise<{ conversationId: string; workflowName: string; rationale: string }> {
  const body: Record<string, unknown> = {};
  if (workflow) body.workflow = workflow;
  return fetchJSON<{ conversationId: string; workflowName: string; rationale: string }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes/${encodeURIComponent(outcomeId)}/execute`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );
}

// ---------------------------------------------------------------------------
// Lifecycle Phase
// ---------------------------------------------------------------------------

export interface PhaseEntry {
  phase: number;
  name: string;
  enteredAt: string;
  exitedAt: string;
  duration: string;
}

export interface LifecyclePhase {
  current: number;
  name: string;
  enteredAt: string;
  history: PhaseEntry[];
}

export async function getLifecyclePhase(
  codebaseId: string
): Promise<{ phase: LifecyclePhase | null }> {
  return fetchJSON<{ phase: LifecyclePhase | null }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/lifecycle-phase`
  );
}

export async function setLifecyclePhase(
  codebaseId: string,
  phase: number
): Promise<LifecyclePhase> {
  return fetchJSON<LifecyclePhase>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/lifecycle-phase`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phase }),
    }
  );
}

export async function createOutcomesFromBlockers(
  codebaseId: string,
  blockers: { title: string }[]
): Promise<{ created: string[]; count: number }> {
  return fetchJSON<{ created: string[]; count: number }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes/from-blockers`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ blockers }),
    }
  );
}

// ---------------------------------------------------------------------------
// Project Mode & Consumption (greenfield / brownfield)
// ---------------------------------------------------------------------------

export type ProjectMode = 'greenfield' | 'brownfield';

export async function getProjectMode(codebaseId: string): Promise<{ mode: ProjectMode | null }> {
  return fetchJSON<{ mode: ProjectMode | null }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/project-mode`
  );
}

export async function setProjectMode(
  codebaseId: string,
  mode: ProjectMode
): Promise<{ mode: ProjectMode; consumedAt: string }> {
  return fetchJSON<{ mode: ProjectMode; consumedAt: string }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/project-mode`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode }),
    }
  );
}

export interface BrownfieldScan {
  hasExistingContext: boolean;
  projectCode: string | null;
  techStack: { language: string; framework?: string; testRunner?: string }[];
  outcomePackets: { id: string; title: string }[];
  adrs: { count: number; sample: string[] };
  tests: { present: boolean; markers: string[] };
  secrets: { present: boolean; findings: { file: string; keys: string[] }[] };
  /** Deploy artifacts detected (root compose + k8s manifests); mapped on brownfield import. */
  deployment: { present: boolean; artifactCount: number };
  hasCodebaseSurvey: boolean;
  suggestedMode: ProjectMode;
  suggestedPhase: number;
  suggestedPhaseName: string;
}

export async function getBrownfieldScan(codebaseId: string): Promise<BrownfieldScan> {
  return fetchJSON<BrownfieldScan>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/brownfield-scan`
  );
}

export interface ReconciliationDecision {
  artifact: string;
  divergence: string;
  decision: string;
  notes?: string;
}

export interface ConsumeProjectRequest {
  mode: ProjectMode;
  templateName?: string;
  importOutcomeTitles?: string[];
  startingPhase?: number;
  reconciliation?: ReconciliationDecision[];
  /** Migrate detected .env secrets into the encrypted per-codebase vault during consume. */
  migrateKeys?: boolean;
}

export interface ConsumeProjectResult {
  mode: ProjectMode;
  createdOutcomeIds: string[];
  lifecyclePhase: number | null;
  reconciliationRecordPath: string | null;
  /** Deployed environments mapped from the repo on brownfield import; null if skipped/failed. */
  deploymentTargetCount: number | null;
  /** Present when migrateKeys was requested. */
  migratedKeys?: { file: string; key: string }[];
  failedKeys?: { file: string; key: string; reason: string }[];
  /** Operator/infra keys detected but left in .env (route to MOS Vault, not the project store). */
  operatorKeys?: { file: string; key: string }[];
  migrationError?: string;
}

export async function consumeProject(
  codebaseId: string,
  request: ConsumeProjectRequest
): Promise<ConsumeProjectResult> {
  return fetchJSON<ConsumeProjectResult>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/consume`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    }
  );
}

export interface PhaseWorkflowRecommendation {
  phase: number;
  phaseName: string;
  mode: ProjectMode | null;
  recommended: string[];
  rationale: string;
}

export async function getRecommendedWorkflows(
  codebaseId: string
): Promise<{ recommendation: PhaseWorkflowRecommendation | null; mode: ProjectMode | null }> {
  return fetchJSON<{
    recommendation: PhaseWorkflowRecommendation | null;
    mode: ProjectMode | null;
  }>(`/api/codebases/${encodeURIComponent(codebaseId)}/recommended-workflows`);
}

// ---------------------------------------------------------------------------
// Delivery Status
// ---------------------------------------------------------------------------

export interface DeliveryActivityEntry {
  outcomeId: string;
  outcomeTitle: string;
  timestamp: string;
  action: string;
  actor: string;
  details?: string;
}

export type DeliveryAttentionReason = 'dependencies' | 'rework' | 'low_convergence';

export interface DeliveryAttentionItem {
  outcome: OutcomeSummary;
  reason: DeliveryAttentionReason;
  hardBlocked: boolean;
  blockedBy: string[];
  message: string;
}

export interface DeliveryStatus {
  lifecyclePhase: LifecyclePhase | null;
  rollup: ProjectRollup;
  attentionItems: DeliveryAttentionItem[];
  blockers: OutcomeSummary[];
  outcomes: OutcomeSummary[];
  recentActivity: DeliveryActivityEntry[];
}

export async function getDeliveryStatus(codebaseId: string): Promise<DeliveryStatus> {
  return fetchJSON<DeliveryStatus>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/delivery-status`
  );
}

// ---------------------------------------------------------------------------
// Test Results & Convergence
// ---------------------------------------------------------------------------

export interface TestResultInput {
  layer: 'unit' | 'integration' | 'e2e' | 'golden' | 'eval';
  passed: number;
  failed: number;
  total: number;
}

export interface ConvergenceResult {
  score: number;
  breakdown: {
    sourceIndependence: number;
    evidenceQuality: number;
    riskFlags: number;
    manual: number;
  };
  suggestedTransition: string | null;
  applied: boolean;
}

export async function submitTestResults(
  codebaseId: string,
  outcomeId: string,
  results: TestResultInput[],
  criteriaAddressed?: number,
  totalCriteria?: number,
  evidenceLinks?: string[]
): Promise<ConvergenceResult> {
  return fetchJSON<ConvergenceResult>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes/${encodeURIComponent(outcomeId)}/test-results`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ results, criteriaAddressed, totalCriteria, evidenceLinks }),
    }
  );
}

export interface ConvergenceHistoryEntry {
  timestamp: string;
  score: number;
  actor: string;
}

export async function getConvergenceHistory(
  codebaseId: string,
  outcomeId: string
): Promise<{ history: ConvergenceHistoryEntry[] }> {
  return fetchJSON<{ history: ConvergenceHistoryEntry[] }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes/${encodeURIComponent(outcomeId)}/convergence-history`
  );
}

export interface ProjectRollup {
  projectCode: string;
  totalOutcomes: number;
  active: number;
  completed: number;
  averageConvergenceScore: number | null;
  outcomes: OutcomeSummary[];
}

export async function getProjectRollup(codebaseId: string): Promise<ProjectRollup> {
  return fetchJSON<ProjectRollup>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/outcomes/rollup`
  );
}

// AI Providers
export interface ProviderInfo {
  id: string;
  provider: string;
  display_name: string;
  auth_status: 'none' | 'authenticated' | 'expired' | 'error';
  auth_method: string | null;
  enabled: boolean;
  last_validated_at: string | null;
  last_used_at: string | null;
  error_message: string | null;
  credential_keys: string[];
}

export async function listProviders(): Promise<{ providers: ProviderInfo[] }> {
  return fetchJSON<{ providers: ProviderInfo[] }>('/api/providers');
}

export async function updateProvider(
  name: string,
  updates: { enabled?: boolean; display_name?: string }
): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(`/api/providers/${encodeURIComponent(name)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
}

export async function storeProviderCredentials(
  name: string,
  method: string,
  credentials: Record<string, string>
): Promise<{ success: boolean; auth_status?: string; error?: string }> {
  return fetchJSON<{ success: boolean; auth_status?: string; error?: string }>(
    `/api/providers/${encodeURIComponent(name)}/credentials`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ method, credentials }),
    }
  );
}

export async function deleteProviderCredentials(name: string): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(`/api/providers/${encodeURIComponent(name)}/credentials`, {
    method: 'DELETE',
  });
}

export async function validateProvider(name: string): Promise<{ valid: boolean; error?: string }> {
  return fetchJSON<{ valid: boolean; error?: string }>(
    `/api/providers/${encodeURIComponent(name)}/validate`,
    { method: 'POST' }
  );
}

export async function importProviderCredentials(
  name: string
): Promise<{ success: boolean; imported_keys?: string[]; error?: string }> {
  return fetchJSON<{ success: boolean; imported_keys?: string[]; error?: string }>(
    `/api/providers/${encodeURIComponent(name)}/import`,
    { method: 'POST' }
  );
}

// System
export async function getHealth(): Promise<HealthResponse> {
  return fetchJSON<HealthResponse>('/api/health');
}

// ---------------------------------------------------------------------------
// G-Brain
// ---------------------------------------------------------------------------

export interface GBrainContextData {
  projectCode: string;
  techStack: { language: string; framework?: string; testRunner?: string }[];
  conventions: Record<string, string>;
  lastScannedAt: string;
  lifecyclePhase?: LifecyclePhase;
}

export interface GBrainPattern {
  timestamp: string;
  category: string;
  data: Record<string, unknown>;
}

export async function getGBrainContext(
  codebaseId: string
): Promise<{ context: GBrainContextData | null }> {
  return fetchJSON<{ context: GBrainContextData | null }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/g-brain/context`
  );
}

export async function getGBrainPatterns(
  codebaseId: string
): Promise<{ patterns: GBrainPattern[] }> {
  return fetchJSON<{ patterns: GBrainPattern[] }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/g-brain/patterns`
  );
}

export async function getGBrainSkills(codebaseId: string): Promise<{ skills: string[] }> {
  return fetchJSON<{ skills: string[] }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/g-brain/skills`
  );
}

export async function analyzeGBrain(codebaseId: string): Promise<{ conversationId: string }> {
  return fetchJSON<{ conversationId: string }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/g-brain/analyze`,
    { method: 'POST' }
  );
}

// ---------------------------------------------------------------------------
// Cross-Project Intelligence
// ---------------------------------------------------------------------------

export interface ProjectIntelligence {
  codebaseId: string;
  name: string;
  cwd: string;
  lifecyclePhase: { current: number; name: string } | null;
  patternCount: number;
  recentPatterns: GBrainPattern[];
}

export interface CrossProjectIntelligence {
  projects: ProjectIntelligence[];
  totalPatterns: number;
  categoryBreakdown: Record<string, number>;
}

export async function getCrossProjectIntelligence(): Promise<CrossProjectIntelligence> {
  return fetchJSON<CrossProjectIntelligence>('/api/intelligence');
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

export interface ProjectTemplate {
  name: string;
  description: string;
  outcomes: { title: string; status: string }[];
  lifecyclePhase?: number;
  skills: string[];
}

export async function listTemplates(): Promise<{ templates: ProjectTemplate[] }> {
  return fetchJSON<{ templates: ProjectTemplate[] }>('/api/templates');
}

export async function applyTemplate(
  codebaseId: string,
  templateName: string
): Promise<{ created: string[]; count: number }> {
  return fetchJSON<{ created: string[]; count: number }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/apply-template`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ templateName }),
    }
  );
}

// ---------------------------------------------------------------------------
// System
// ---------------------------------------------------------------------------

export type UpdateCheckResult = components['schemas']['UpdateCheckResponse'];

export async function getUpdateCheck(): Promise<UpdateCheckResult> {
  return fetchJSON<UpdateCheckResult>('/api/update-check');
}

// ---------------------------------------------------------------------------
// Multi-Repo Projects (Phase 1)
// ---------------------------------------------------------------------------

export interface ProjectResponse {
  id: string;
  project_code: string;
  name: string;
  organization_name: string | null;
  description: string | null;
  legacy_code_alias: string | null;
  primary_codebase_id: string | null;
  metadata: Record<string, unknown>;
  org_id: string | null;
  user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectRepoResponse {
  id: string;
  project_id: string;
  codebase_id: string;
  role: 'primary' | 'member';
  outcomes_path: string | null;
  outcomes_path_convention: string | null;
  enrollment_project_code: string | null;
  org_id: string | null;
  user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectWithReposResponse extends ProjectResponse {
  repos: ProjectRepoResponse[];
}

export interface ProjectHealthIssue {
  type: 'unlinked_enrollment' | 'broken_parent_link' | 'project_no_repos';
  code: string;
  detail: string;
}

export interface ProjectHealthResponse {
  healthy: boolean;
  summary: {
    totalProjects: number;
    totalEnrollments: number;
    linkedEnrollments: number;
    unlinkendEnrollments: number;
    projectsWithNoRepos: number;
    enrollmentsWithBrokenParent: number;
  };
  issues: ProjectHealthIssue[];
}

export async function listProjects(): Promise<ProjectResponse[]> {
  return fetchJSON<ProjectResponse[]>('/api/projects');
}

export async function getProjectWithRepos(id: string): Promise<ProjectWithReposResponse> {
  return fetchJSON<ProjectWithReposResponse>(`/api/projects/${id}`);
}

export async function getProjectHealth(): Promise<ProjectHealthResponse> {
  return fetchJSON<ProjectHealthResponse>('/api/projects/health');
}

export type RepositoryLineageResponse = components['schemas']['RepositoryLineage'];
export type RepositoryLifecycleRunResponse = components['schemas']['RepositoryLifecycleRun'];
export type ProvisionFromProductRequest = components['schemas']['ProvisionFromProductRequest'];
export type ProvisionFromProductResponse = components['schemas']['ProvisionFromProductResponse'];
export type RepositorySyncRequest = components['schemas']['RepositorySyncRequest'];
export type RepositorySyncResult = components['schemas']['RepositorySyncResult'];
export type RepositoryPromotionRequest = components['schemas']['RepositoryPromotionRequest'];
export type RepositoryPromotionResult = components['schemas']['RepositoryPromotionResult'];

export async function provisionFromProduct(
  input: ProvisionFromProductRequest
): Promise<ProvisionFromProductResponse> {
  return fetchJSON('/api/projects/provision-from-product', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function synchronizeRepository(
  projectId: string,
  input: RepositorySyncRequest
): Promise<RepositorySyncResult> {
  return fetchJSON(`/api/projects/${encodeURIComponent(projectId)}/upstream-syncs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function promoteRepositoryChanges(
  projectId: string,
  input: RepositoryPromotionRequest
): Promise<RepositoryPromotionResult> {
  return fetchJSON(`/api/projects/${encodeURIComponent(projectId)}/upstream-promotions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function getRepositoryLineage(
  projectId: string
): Promise<RepositoryLineageResponse | null> {
  try {
    return await fetchJSON<RepositoryLineageResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/repository-lineage`
    );
  } catch (error) {
    if (error instanceof Error && 'status' in error && error.status === 404) return null;
    throw error;
  }
}

export async function listRepositoryLifecycleRuns(
  projectId: string
): Promise<RepositoryLifecycleRunResponse[]> {
  return fetchJSON<RepositoryLifecycleRunResponse[]>(
    `/api/projects/${encodeURIComponent(projectId)}/upstream-syncs`
  );
}

// ---------------------------------------------------------------------------
// MOS Audit Federation (Settings panel — Piece 5 of audit federation UI)
// ---------------------------------------------------------------------------

/**
 * TODO(audit-fed-ui): Regenerate api.generated.d.ts and replace the four
 * hand-rolled MosFederation* interfaces below with re-exports from the
 * generated types, e.g.:
 *
 *   export type MosFederationStatusResponse =
 *     components['schemas']['MosFederationStatusResponse'];
 *
 * Regeneration requires the dev server running at port 3090:
 *   bun run dev:server  # needs DATABASE_URL pointing at a live Postgres
 *   bun --filter @archon/web generate:types
 *
 * Blocker: the Maestro Postgres container (localhost:15432) was not running
 * during the P5 fix-up pass (2026-06-02) and the docker socket was not
 * accessible in the Claude Code environment. The hand-rolled shapes match the
 * server schemas exactly (see mosFederationStatusResponseSchema in
 * packages/server/src/routes/schemas/mos-audit-federation.schemas.ts) — safe
 * to ship as-is; replace when the server can boot for a local type-gen run.
 */

/**
 * Status returned by GET / PUT / DELETE.
 *
 * The shape mirrors `mosFederationStatusResponseSchema` in
 * packages/server/src/routes/schemas/mos-audit-federation.schemas.ts. The
 * hmacSecret is never returned — `hasSecret` is the boolean signal.
 *
 * - `enabled` / `hasSecret` reflect what's in the vault (config-time state)
 * - `runtimeEnabled` / `runtimeReason` reflect what the forwarder cache
 *   currently has loaded (run-time state). They can diverge briefly during
 *   reload or when vault config is invalid.
 */
// TODO(audit-fed-ui): replace with components['schemas']['MosFederationStatusResponse'] after type regen
export interface MosFederationStatusResponse {
  enabled: boolean;
  hasSecret: boolean;
  serviceId?: string;
  forwardUrl?: string;
  authMode?: 'disabled' | 'mos-oidc';
  runtimeEnabled: boolean;
  runtimeReason?: string;
}

// TODO(audit-fed-ui): replace with components['schemas']['MosFederationUpdateBody'] after type regen
export interface MosFederationUpdateBody {
  serviceId: string;
  hmacSecret: string;
  forwardUrl: string;
  authMode: 'disabled' | 'mos-oidc';
  propagateToMos: boolean;
}

// TODO(audit-fed-ui): replace with components['schemas']['MosFederationTestResponse'] after type regen
export interface MosFederationTestResponse {
  ok: boolean;
  httpStatus?: number;
  error?: string;
}

// TODO(audit-fed-ui): replace with components['schemas']['MosFederationGenerateSecretResponse'] after type regen
export interface MosFederationGenerateSecretResponse {
  secret: string;
}

// --- Operator org (Settings panel) ------------------------------------------
// TODO(operator-org-ui): replace with components['schemas'][...] after type regen.
const OPERATOR_ORG_PATH = '/api/integrations/operator-org';

export interface OperatorOrgStatusResponse {
  orgId: string;
  vaultConfigured: boolean;
  vaultStatus: 'unconfigured' | 'unreachable' | 'empty' | 'ok';
  vaultValue: string | null;
  envDefault: string | null;
}
export interface OperatorOrgUpdateBody {
  orgId: string;
}

export async function getOperatorOrgStatus(): Promise<OperatorOrgStatusResponse> {
  return fetchJSON<OperatorOrgStatusResponse>(OPERATOR_ORG_PATH);
}

export async function updateOperatorOrg(
  body: OperatorOrgUpdateBody
): Promise<OperatorOrgStatusResponse> {
  return fetchJSON<OperatorOrgStatusResponse>(OPERATOR_ORG_PATH, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** What the two managed-secret routes below will actually accept, read from the
 *  server rather than restated here.
 *
 *  There used to be two hand-maintained constants in this file: a 7-key copy of
 *  MANAGED_CONFIG_KEYS (the server had 8) and a 2-entry ROTATABLE_CLASSES (the
 *  server accepted 4). Both POST routes are plain `app.post` handlers outside
 *  the OpenAPI document, so `check:contract-sync` had nothing to diff and the
 *  drift was invisible — half the rotatable classes were unreachable from the
 *  UI, and the Key dropdown offered a key the server rejects every time. The
 *  membership rules live next to the data they filter, in
 *  @maestro/core/security; this type comes from the generated contract. */
export type ManagedConfigCatalog = components['schemas']['ManagedConfigCatalogResponse'];
export type RotatableClass = components['schemas']['RotatableClass'];

export async function fetchManagedConfigCatalog(): Promise<ManagedConfigCatalog> {
  return fetchJSON<ManagedConfigCatalog>('/api/config/managed-catalog');
}

/** Enqueue an approval-gated change to a managed config key. The value is sent
 *  over the authenticated admin session and handed to the server's in-memory
 *  pending store; the operator then approves the change from Telegram. Returns
 *  the pending approval id. */
export async function requestManagedConfigChange(
  key: string,
  value: string
): Promise<{ id: string; status: 'pending' }> {
  return fetchJSON<{ id: string; status: 'pending' }>('/api/config/managed-secret/request', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, value }),
  });
}

/** Enqueue an approval-gated rotation of a shared managed secret. The server
 *  generates the new value; the operator approves the change from Telegram.
 *  Returns the pending approval id. */
export async function rotateManagedSecret(
  classId: string
): Promise<{ id: string; status: string }> {
  return fetchJSON<{ id: string; status: string }>('/api/config/managed-secret/rotate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ classId }),
  });
}

/** Terminal states stop the UI polling; `pending`/`claimed` keep it going. */
export type ProvisionStatus = 'pending' | 'claimed' | 'completed' | 'expired' | 'failed';

export const PROVISION_TERMINAL_STATUSES: readonly ProvisionStatus[] = [
  'completed',
  'failed',
  'expired',
];

export function isProvisionTerminal(status: string): boolean {
  return (PROVISION_TERMINAL_STATUSES as readonly string[]).includes(status);
}

export interface ProvisionRequestResult {
  id: string;
  status: string;
  plan: { roleName: string; policyName: string; policyFile: string }[];
  /** Present on the status read; absent on the initial create response. */
  createdAt?: number;
  expiresAt?: number;
}
export async function requestProvision(): Promise<ProvisionRequestResult> {
  return fetchJSON<ProvisionRequestResult>('/api/provision/request', { method: 'POST' });
}

/**
 * Read a provision request's status. Hits `/api/provision/{id}`, NOT
 * `/api/provision/{id}/plan` — the latter marks the request `claimed`, so polling it
 * would make the UI trigger the transition it is meant to observe.
 *
 * Throws on 404, which is the expected outcome after a server restart: the request
 * store is an in-memory Map, so ids do not survive one.
 */
export async function getProvisionStatus(id: string): Promise<ProvisionRequestResult> {
  return fetchJSON<ProvisionRequestResult>(`/api/provision/${encodeURIComponent(id)}`);
}

const MOS_FEDERATION_PATH = '/api/integrations/mos-audit-federation';

export async function getMosAuditFederationStatus(): Promise<MosFederationStatusResponse> {
  return fetchJSON<MosFederationStatusResponse>(MOS_FEDERATION_PATH);
}

/**
 * Update the federation config.
 *
 * If `body.propagateToMos === true`, the server route ALSO forwards a
 * POST to MOS /api/admin/service-tokens and requires the operator's
 * bearer token in the Authorization header. The operator supplies that
 * token via the UI form (the archon_session cookie is HttpOnly and can't
 * be read from JS).
 *
 * When `propagateToMos === false`, `mosBearerToken` is ignored.
 */
export async function updateMosAuditFederation(
  body: MosFederationUpdateBody,
  mosBearerToken?: string
): Promise<MosFederationStatusResponse> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (body.propagateToMos && mosBearerToken && mosBearerToken.trim().length > 0) {
    const trimmed = mosBearerToken.trim();
    headers.Authorization = trimmed.toLowerCase().startsWith('bearer ')
      ? trimmed
      : `Bearer ${trimmed}`;
  }
  return fetchJSON<MosFederationStatusResponse>(MOS_FEDERATION_PATH, {
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  });
}

export async function testMosAuditFederation(): Promise<MosFederationTestResponse> {
  return fetchJSON<MosFederationTestResponse>(`${MOS_FEDERATION_PATH}/test`, { method: 'POST' });
}

/**
 * Best-effort DELETE — also clears the MOS-side registration when the
 * operator supplies a bearer token. When `mosBearerToken` is omitted, only
 * the local vault is cleared (server logs but doesn't fail).
 */
export async function disableMosAuditFederation(
  mosBearerToken?: string
): Promise<MosFederationStatusResponse> {
  const headers: Record<string, string> = {};
  if (mosBearerToken && mosBearerToken.trim().length > 0) {
    const trimmed = mosBearerToken.trim();
    headers.Authorization = trimmed.toLowerCase().startsWith('bearer ')
      ? trimmed
      : `Bearer ${trimmed}`;
  }
  return fetchJSON<MosFederationStatusResponse>(MOS_FEDERATION_PATH, {
    method: 'DELETE',
    headers,
  });
}

export async function generateMosAuditSecret(): Promise<MosFederationGenerateSecretResponse> {
  return fetchJSON<MosFederationGenerateSecretResponse>(`${MOS_FEDERATION_PATH}/generate-secret`, {
    method: 'POST',
  });
}

// ---------------------------------------------------------------------------
// Phase 5: Deployment Pipeline (Status + Test Emit)
// ---------------------------------------------------------------------------

export interface DeploymentPipelineStatusResponse {
  secrets: Record<string, 'configured' | 'missing'>;
  overallStatus: 'ready' | 'partial' | 'unconfigured';
  recentManifests: {
    manifestId: string;
    outcomeId: string;
    environment: string;
    status: 'succeeded' | 'failed';
    deployedAt: string;
  }[];
  lastCheckedAt: string;
}

export interface DeploymentPipelineTestResponse {
  dispatched: boolean;
  eventId?: string;
  missingSecrets?: string[];
  error?: string;
}

const DEPLOYMENT_PIPELINE_PATH = '/api/integrations/deployment-pipeline';

export async function getPipelineStatus(): Promise<DeploymentPipelineStatusResponse> {
  return fetchJSON<DeploymentPipelineStatusResponse>(DEPLOYMENT_PIPELINE_PATH);
}

export async function testPipelineEmit(): Promise<DeploymentPipelineTestResponse> {
  return fetchJSON<DeploymentPipelineTestResponse>(`${DEPLOYMENT_PIPELINE_PATH}/test`, {
    method: 'POST',
  });
}

// ---------------------------------------------------------------------------
// Deploy-Mesh Setup (admin) — wraps the proven tunneled-deploy flow.
// Server: packages/server/src/routes/api/integrations/mesh-setup.ts.
// Types hand-written to mirror mesh-setup.schemas.ts (the sibling
// deployment-pipeline types follow the same convention until schema regen).
// ---------------------------------------------------------------------------

export interface MeshConfigPresence {
  paired: boolean;
  agentId?: string;
  agentSlug?: string;
  product?: string;
  tunnelUrl?: string;
  pairedAt?: string;
}

export interface RegisterMeshAgentRequest {
  slug: string;
  displayName: string;
  product?: string;
  orgId?: string;
  operatorId?: string;
  meshBaseUrl?: string;
  forge?: string;
  ownerRepo?: string;
}

/** Register response — carries freshly-minted secrets exactly ONCE. */
export interface RegisterMeshAgentResponse {
  agent: { id: string; slug: string; product: string; tunnelUrl?: string };
  operatorUrl: string;
  meshConfigPath: string;
  oneTimeReveal: true;
  forwardToken: string;
  operatorSigningKey?: string;
  webhookSecret?: string;
}

export interface DeployTarget {
  id: string;
  outcomeId: string;
  targetName: string;
  environment: string;
  targetType: 'compose' | 'k8s';
  executionMode: 'direct' | 'tunneled';
  executionConfig: Record<string, unknown>;
  healthCheck: {
    url: string;
    timeoutSeconds: number;
    smokeTest?: { path: string; expectedStatus: number };
  };
  alertConfig?: Record<string, unknown>;
}

export interface DeployTargetsListResponse {
  targets: DeployTarget[];
}

export async function getMeshConfig(): Promise<MeshConfigPresence> {
  return fetchJSON<MeshConfigPresence>('/api/mesh/config');
}

export async function registerMeshRemoteAgent(
  input: RegisterMeshAgentRequest
): Promise<RegisterMeshAgentResponse> {
  return fetchJSON<RegisterMeshAgentResponse>('/api/mesh/remote-agent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function listDeployTargets(): Promise<DeployTargetsListResponse> {
  return fetchJSON<DeployTargetsListResponse>('/api/deploy/targets');
}

export async function createDeployTarget(input: Omit<DeployTarget, 'id'>): Promise<DeployTarget> {
  return fetchJSON<DeployTarget>('/api/deploy/targets', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}
